import type { LiftingPlan, RamsDocumentType, RamsRecord } from '../../types/healthSafety.ts';

export const EMPTY_LIFTING_PLAN: LiftingPlan = {
  complexity: 'non_routine',
  liftDescription: '',
  appointedPerson: '',
  liftSupervisor: '',
  operator: '',
  slingerSignaller: '',
  communicationMethod: 'Clear hand signals; radio where line of sight is lost',
  machineMakeModel: '',
  machineId: '',
  configuration: '',
  attachment: '',
  loadChartReference: '',
  maxRatedCapacityKg: null,
  ratedCapacityAtPositionKg: null,
  loadDescription: '',
  loadWeightKg: null,
  accessoryWeightKg: null,
  loadDimensions: '',
  centreOfGravity: '',
  liftingPoints: '',
  loadCentreMm: null,
  liftRadiusM: null,
  liftHeightM: null,
  pickPoint: '',
  setDownPoint: '',
  travelRoute: '',
  groundConditions: '',
  groundBearingCapacity: '',
  maximumGradient: '',
  windLimitMph: null,
  overheadServices: '',
  undergroundServices: '',
  exclusionZone: '',
  visibility: '',
  tagLines: '',
  emergencyProcedure: '',
  accessories: [],
  loadChartVerified: false,
  machineChecksComplete: false,
  thoroughExaminationVerified: false,
  accessoriesVerified: false,
  groundVerified: false,
  exclusionZoneReady: false,
  briefingComplete: false,
};

export function resolveDocumentType(record: Pick<RamsRecord, 'documentType'>): RamsDocumentType {
  return record.documentType === 'lifting_plan' ? 'lifting_plan' : 'rams';
}

export function resolveLiftingPlan(plan: LiftingPlan | undefined): LiftingPlan {
  return {
    ...EMPTY_LIFTING_PLAN,
    ...(plan ?? {}),
    accessories: plan?.accessories ?? [],
  };
}

export function liftTotalWeightKg(plan: LiftingPlan): number | null {
  if (plan.loadWeightKg == null && plan.accessoryWeightKg == null) return null;
  return (plan.loadWeightKg ?? 0) + (plan.accessoryWeightKg ?? 0);
}

export function liftUtilisationPercent(plan: LiftingPlan): number | null {
  const total = liftTotalWeightKg(plan);
  if (total == null || !plan.ratedCapacityAtPositionKg || plan.ratedCapacityAtPositionKg <= 0) return null;
  return Math.round((total / plan.ratedCapacityAtPositionKg) * 100);
}

export function liftingPlanBlockers(plan: LiftingPlan): string[] {
  const reasons: string[] = [];
  if (!plan.appointedPerson.trim() || !plan.liftSupervisor.trim() || !plan.operator.trim()) {
    reasons.push('the lift team');
  }
  if (!plan.machineMakeModel.trim() || !plan.attachment.trim() || !plan.loadChartReference.trim()) {
    reasons.push('the machine configuration and load chart');
  }
  if (!plan.loadDescription.trim() || plan.loadWeightKg == null) reasons.push('the verified load weight');
  if (plan.ratedCapacityAtPositionKg == null || plan.liftRadiusM == null || plan.liftHeightM == null) {
    reasons.push('capacity, radius and lift height');
  }
  if (!plan.groundConditions.trim() || !plan.exclusionZone.trim()) reasons.push('site and exclusion-zone controls');
  if (!plan.emergencyProcedure.trim()) reasons.push('the lifting emergency procedure');
  // The boolean hold points record checks completed by the operator or lift
  // supervisor on site. They must stop the lift when incomplete, but they are
  // not authoring-time information and must not block sending the plan for
  // review. The plan itself still has to define the relevant controls above.
  return reasons;
}
